package ai.wagemix.net.client

import io.ktor.http.HttpHeaders
import com.fsryan.foundation.net.client.addHeader
import com.fsryan.foundation.net.client.FSAccessTokenFetchFailedException
import ai.wagemix.net.models.KxsWMAccountCreated
import ai.wagemix.net.models.KxsWMArea
import ai.wagemix.net.models.KxsWMJobDescriptionFile
import ai.wagemix.net.models.KxsWMPasswordResetTarget
import ai.wagemix.net.models.WMAccountCreate
import ai.wagemix.net.models.WMAccountCreated
import ai.wagemix.net.models.WMAuthActionToken
import ai.wagemix.net.models.WMPasswordResetConfirm
import ai.wagemix.net.models.WMPasswordResetRequest
import ai.wagemix.net.models.WMPasswordResetTarget
import ai.wagemix.net.models.KxsWMCheckoutSummary
import ai.wagemix.net.models.KxsWMFileUploadUpdate
import ai.wagemix.net.models.KxsWMIndustry
import ai.wagemix.net.models.KxsWMIntegerChartData
import ai.wagemix.net.models.KxsWMIntegerDataSeriesData
import ai.wagemix.net.models.KxsWMOccupationTaxonomy
import ai.wagemix.net.models.KxsWMPlanPrice
import ai.wagemix.net.models.KxsWMRedirectUrl
import ai.wagemix.net.models.KxsWMSharedMixResponse
import ai.wagemix.net.models.KxsWMStatsCollection
import ai.wagemix.net.models.KxsWMYearBounds
import ai.wagemix.net.models.KxsWMMcpApiKey
import ai.wagemix.net.models.KxsWMMcpApiKeyCreated
import ai.wagemix.net.models.KxsWMMcpApiKeyOwner
import ai.wagemix.net.models.WMMcpApiKey
import ai.wagemix.net.models.WMMcpApiKeyCreate
import ai.wagemix.net.models.WMMcpApiKeyCreated
import ai.wagemix.net.models.WMMcpApiKeyOwner
import ai.wagemix.net.models.WMMcpApiKeyResolve
import ai.wagemix.net.models.WMArea
import ai.wagemix.net.models.WMBillingPortalSessionCreate
import ai.wagemix.net.models.WMCheckoutSessionCreate
import ai.wagemix.net.models.WMCheckoutSummary
import ai.wagemix.net.models.WMFileUploadUpdate
import ai.wagemix.net.models.WMIndustry
import ai.wagemix.net.models.WMIntegerChartData
import ai.wagemix.net.models.WMIntegerDataSeriesData
import ai.wagemix.net.models.WMJDUrlUpload
import ai.wagemix.net.models.WMOccupationTaxonomy
import ai.wagemix.net.models.WMPlanPrice
import ai.wagemix.net.models.WMRedirectUrl
import ai.wagemix.net.models.WMReevaluateJD
import ai.wagemix.net.models.WMSharedMixCreate
import ai.wagemix.net.models.WMSharedMixResponse
import ai.wagemix.net.models.WMStatsCollection
import ai.wagemix.net.models.WMYearBounds
import ai.wagemix.net.models.toKxs
import ai.wagemix.net.models.WMJobDescriptionFile
import ai.wagemix.net.models.WMSharedMixActivation
import com.fsryan.foundation.net.client.FSAuthorizedRequester
import com.fsryan.foundation.net.client.FSRequest
import com.fsryan.foundation.net.client.FSResponse
import com.fsryan.foundation.net.client.bearerAuthorization
import com.fsryan.foundation.net.client.headerValues
import com.fsryan.foundation.net.client.ktor.FSBaseKtorClientApi
import com.fsryan.foundation.net.client.url
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.sse.ClientSSESession
import io.ktor.client.plugins.sse.serverSentEvents
import io.ktor.client.request.forms.MultiPartFormDataContent
import io.ktor.client.request.forms.append
import io.ktor.client.request.forms.formData
import io.ktor.client.request.header
import io.ktor.client.request.setBody
import io.ktor.client.request.url
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.utils.io.core.writeFully
import kotlinx.coroutines.cancel
import kotlinx.serialization.json.Json

/**
 * The Wagemix API. Authenticated endpoints obtain the signed-in user's bearer token from the
 * [FSAuthorizedRequester] the client was built with, so callers never handle tokens; a request
 * that already carries an `Authorization` header is sent as is.
 */
interface WagemixClientAPI {
    /** The first and last year of statistics loaded; answers 503 while the API cannot read them. */
    suspend fun fetchYearBounds(modify: WMYearBoundsRequestBuilder.() -> Unit = {}): FSResponse<WMYearBounds, Nothing>

    suspend fun fetchOccupationsTaxonomy(
        modify: WMOccupationsRequestBuilder.() -> Unit = {}
    ): FSResponse<WMOccupationTaxonomy, Nothing>
    suspend fun fetchAreasWithData(
        modify: WMAreasWithDataRequestBuilder.() -> Unit = {}
    ): FSResponse<List<WMArea>, Nothing>
    suspend fun fetchIndustriesWithData(
        modify: WMIndustriesWithDataRequestBuilder.() -> Unit = {}
    ): FSResponse<List<WMIndustry>, Nothing>
    suspend fun fetchAnnualOccupationStatsChart(
        modify: WMOccupationStatsAnnualChartRequestBuilder.() -> Unit
    ): FSResponse<WMIntegerChartData, Nothing>
    suspend fun fetchAnnualOccupationStatsDataSeries(
        modify: WMOccupationStatsAnnualDataSeriesRequestBuilder.() -> Unit
    ): FSResponse<List<WMIntegerDataSeriesData>, Nothing>
    suspend fun fetchOccupationStatsCollection(
        modify: WMOccupationStatsCollectionRequestBuilder.() -> Unit
    ): FSResponse<WMStatsCollection, Nothing>

    suspend fun uploadFile(
        modify: WMJobDescriptionFileUploadRequestBuilder.() -> Unit
    ): FSResponse<Nothing, FileUploadRequestBody>

    suspend fun uploadJDUrl(
        modify: WMJobDescriptionUrlUploadRequestBuilder.() -> Unit
    ): FSResponse<Nothing, WMJDUrlUpload>

    suspend fun reevaluateJD(
        modify: WMJobDescriptionReevaluateRequestBuilder.() -> Unit
    ): FSResponse<Nothing, WMReevaluateJD>

    suspend fun createFileEventStream(
        modify: WMFileEventStreamRequestBuilder.() -> Unit,
        block: suspend ClientSSESession.() -> Unit = {}
    )

    /**
     * Follows the pipeline's progress stream for [eventStreamId], calling [block] for each update
     * and returning once the final (not in progress) update has been delivered.
     */
    suspend fun createFileUploadUpdateListener(eventStreamId: String, block: suspend (WMFileUploadUpdate) -> Unit)

    /** The stored job descriptions behind the caller's mixes, by mix id, with preview and download links. */
    suspend fun fetchStoredMixJDFileUrls(
        modify: WMPresignedJDFileUrlsRequestBuilder.() -> Unit
    ): FSResponse<Map<String, WMJobDescriptionFile>, Nothing>

    suspend fun createSharedMixUrl(
        modify: WMCreateSharedMixRequestBuilder.() -> Unit
    ): FSResponse<WMSharedMixResponse, WMSharedMixCreate>

    /** Runs one of the caller's failed job-description uploads again; succeeds with 202. */
    suspend fun retryJobDescriptionUpload(modify: WMRetryUploadRequestBuilder.() -> Unit): FSResponse<Unit, Nothing>

    /** Deletes one of the caller's failed job-description uploads and its files; succeeds with 204. */
    suspend fun deleteJobDescriptionUpload(modify: WMDeleteUploadRequestBuilder.() -> Unit): FSResponse<Unit, Nothing>

    /** Turns one of the caller's share links on or off; succeeds with 204. */
    suspend fun setSharedMixActive(
        modify: WMSetSharedMixActiveRequestBuilder.() -> Unit
    ): FSResponse<Unit, WMSharedMixActivation>

    /** Renders one of the caller's shared mixes' preview image again; succeeds with 202. */
    suspend fun regenerateSharedMixPreview(
        modify: WMRegenerateSharedMixPreviewRequestBuilder.() -> Unit
    ): FSResponse<Unit, Nothing>

    suspend fun fetchPlanPrices(modify: WMPlanPricesRequestBuilder.() -> Unit = {}): FSResponse<List<WMPlanPrice>, Nothing>

    suspend fun createCheckoutSession(
        modify: WMCreateCheckoutSessionRequestBuilder.() -> Unit
    ): FSResponse<WMRedirectUrl, WMCheckoutSessionCreate>

    suspend fun createBillingPortalSession(
        modify: WMCreateBillingPortalSessionRequestBuilder.() -> Unit
    ): FSResponse<WMRedirectUrl, WMBillingPortalSessionCreate>

    suspend fun fetchCheckoutSession(modify: WMCheckoutSessionRequestBuilder.() -> Unit): FSResponse<WMCheckoutSummary, Nothing>

    suspend fun createAccount(
        modify: WMCreateAccountRequestBuilder.() -> Unit
    ): FSResponse<WMAccountCreated, WMAccountCreate>

    /** Succeeds with 202 when an email was sent and 204 when the email is verified already. */
    suspend fun resendEmailVerification(
        modify: WMResendEmailVerificationRequestBuilder.() -> Unit = {}
    ): FSResponse<Unit, Nothing>

    suspend fun confirmEmailVerification(
        modify: WMConfirmEmailVerificationRequestBuilder.() -> Unit
    ): FSResponse<Unit, WMAuthActionToken>

    suspend fun requestPasswordReset(
        modify: WMRequestPasswordResetRequestBuilder.() -> Unit
    ): FSResponse<Unit, WMPasswordResetRequest>

    suspend fun verifyPasswordReset(
        modify: WMVerifyPasswordResetRequestBuilder.() -> Unit
    ): FSResponse<WMPasswordResetTarget, WMAuthActionToken>

    suspend fun confirmPasswordReset(
        modify: WMConfirmPasswordResetRequestBuilder.() -> Unit
    ): FSResponse<Unit, WMPasswordResetConfirm>

    /** A new MCP API key for the signed-in PRO user; 201 with the key, 402 when not PRO, 409 at the key limit. */
    suspend fun createMcpApiKey(
        modify: WMCreateMcpApiKeyRequestBuilder.() -> Unit
    ): FSResponse<WMMcpApiKeyCreated, WMMcpApiKeyCreate>

    /** The signed-in user's unrevoked MCP API keys, newest first. */
    suspend fun fetchMcpApiKeys(
        modify: WMMcpApiKeysRequestBuilder.() -> Unit = {}
    ): FSResponse<List<WMMcpApiKey>, Nothing>

    /** Revokes one of the signed-in user's MCP API keys; succeeds with 204. */
    suspend fun revokeMcpApiKey(modify: WMRevokeMcpApiKeyRequestBuilder.() -> Unit): FSResponse<Unit, Nothing>

    /** The owner and plan of a live MCP API key; for the MCP server, which sets its service token as the bearer. */
    suspend fun resolveMcpApiKey(
        modify: WMResolveMcpApiKeyRequestBuilder.() -> Unit
    ): FSResponse<WMMcpApiKeyOwner, WMMcpApiKeyResolve>
}

/**
 * @param authorizedRequester supplies the bearer token for authenticated endpoints; the default
 * refuses every authenticated call, which is right for anonymous tools such as the CLI's lookups.
 */
fun WagemixClientAPI(
    httpClient: HttpClient,
    baseUrl: String,
    authorizedRequester: FSAuthorizedRequester = FSAuthorizedRequester.unauthorized(baseUrl)
): WagemixClientAPI {
    return WagemixClientAPIImpl(httpClient, baseUrl, authorizedRequester)
}

private class WagemixClientAPIImpl(
    httpClient: HttpClient,
    private val baseUrl: String,
    authorizedRequester: FSAuthorizedRequester
): FSBaseKtorClientApi(httpClient, authorizedRequester), WagemixClientAPI {

    override suspend fun baseUrl(): String = baseUrl

    override suspend fun fetchYearBounds(modify: WMYearBoundsRequestBuilder.() -> Unit): FSResponse<WMYearBounds, Nothing> {
        return createAndSendRequestWithoutBody(
            createRequestBuilder = ::WMYearBoundsRequestBuilder,
            modifyRequest = modify,
            parseResponseBody = { response -> response.body<KxsWMYearBounds>() }
        )
    }

    override suspend fun fetchOccupationsTaxonomy(
        modify: WMOccupationsRequestBuilder.() -> Unit
    ): FSResponse<WMOccupationTaxonomy, Nothing> {
        return createAndSendRequestWithoutBody(
            createRequestBuilder = ::WMOccupationsRequestBuilder,
            modifyRequest = modify,
            parseResponseBody = { response -> response.body<KxsWMOccupationTaxonomy>() }
        )
    }

    override suspend fun fetchAreasWithData(modify: WMAreasWithDataRequestBuilder.() -> Unit): FSResponse<List<WMArea>, Nothing> {
        return createAndSendRequestWithoutBody(
            createRequestBuilder = ::WMAreasWithDataRequestBuilder,
            modifyRequest = modify,
            parseResponseBody = { response -> response.body<List<KxsWMArea>>() }
        )
    }

    override suspend fun fetchIndustriesWithData(modify: WMIndustriesWithDataRequestBuilder.() -> Unit): FSResponse<List<WMIndustry>, Nothing> {
        return createAndSendRequestWithoutBody(
            createRequestBuilder = ::WMIndustriesWithDataRequestBuilder,
            modifyRequest = modify,
            parseResponseBody = { response -> response.body<List<KxsWMIndustry>>() }
        )
    }

    override suspend fun fetchAnnualOccupationStatsChart(
        modify: WMOccupationStatsAnnualChartRequestBuilder.() -> Unit
    ): FSResponse<WMIntegerChartData, Nothing> {
        return createAndSendRequestWithoutBody(
            createRequestBuilder = ::WMOccupationStatsAnnualChartRequestBuilder,
            modifyRequest = modify,
            parseResponseBody = { response -> response.body<KxsWMIntegerChartData>() }
        )
    }

    override suspend fun fetchAnnualOccupationStatsDataSeries(
        modify: WMOccupationStatsAnnualDataSeriesRequestBuilder.() -> Unit
    ): FSResponse<List<WMIntegerDataSeriesData>, Nothing> {
        return createAndSendRequestWithoutBody(
            createRequestBuilder = ::WMOccupationStatsAnnualDataSeriesRequestBuilder,
            modifyRequest = modify,
            parseResponseBody = { response -> response.body<List<KxsWMIntegerDataSeriesData>>() }
        )
    }

    /**
     * Sends the signed-in user's token, so a filtered or weighted chart can be checked against their plan (paid-feature
     * plan W4). With nobody signed in the requester has no token, and the chart is asked for without one.
     */
    override suspend fun fetchOccupationStatsCollection(
        modify: WMOccupationStatsCollectionRequestBuilder.() -> Unit
    ): FSResponse<WMStatsCollection, Nothing> {
        val bearer = signedInTokenOrNull()?.let { token -> "Bearer $token" }
        return createAndSendRequestWithoutBody(
            createRequestBuilder = ::WMOccupationStatsCollectionRequestBuilder,
            modifyRequest = {
                modify()
                bearer?.let { addHeader(HttpHeaders.Authorization, it) }
            },
            parseResponseBody = { response -> response.body<KxsWMStatsCollection>() }
        )
    }

    /** The signed-in user's token, or `null` when the requester has none (nobody is signed in). */
    private suspend fun signedInTokenOrNull(): String? {
        return try {
            authorizedRequester.wrapAuthorizedOperation { _, token -> token }
        } catch (e: FSAccessTokenFetchFailedException) {
            null
        }
    }

    override suspend fun uploadFile(modify: WMJobDescriptionFileUploadRequestBuilder.() -> Unit): FSResponse<Nothing, FileUploadRequestBody> {
        return createAndSendAuthenticatedRequest(
            createRequestBuilder = ::WMJobDescriptionFileUploadRequestBuilder,
            modifyRequest = modify,
            setRequestBody = { requestBody ->
                setBody(
                    MultiPartFormDataContent(
                        formData {
                            append(
                                key = "file",
                                filename = requestBody.fileName,
                                contentType = ContentType.parse(requestBody.contentType),
                                size = requestBody.fileContent.size.toLong()
                            ) {
                                writeFully(requestBody.fileContent)
                            }
                        }
                    )
                )
                contentType(ContentType.MultiPart.FormData)
            },
            parseResponseBody = { null }
        )
    }

    override suspend fun uploadJDUrl(
        modify: WMJobDescriptionUrlUploadRequestBuilder.() -> Unit
    ): FSResponse<Nothing, WMJDUrlUpload> {
        return createAndSendAuthenticatedRequest(
            createRequestBuilder = ::WMJobDescriptionUrlUploadRequestBuilder,
            modifyRequest = modify,
            setRequestBody = { setBody(it.toKxs()) },
            parseResponseBody = { null }
        )
    }

    override suspend fun reevaluateJD(
        modify: WMJobDescriptionReevaluateRequestBuilder.() -> Unit
    ): FSResponse<Nothing, WMReevaluateJD> {
        return createAndSendAuthenticatedRequest(
            createRequestBuilder = ::WMJobDescriptionReevaluateRequestBuilder,
            modifyRequest = modify,
            setRequestBody = { setBody(it.toKxs()) },
            parseResponseBody = { null }
        )
    }

    override suspend fun createFileEventStream(
        modify: WMFileEventStreamRequestBuilder.() -> Unit,
        block: suspend ClientSSESession.() -> Unit
    ) {
        val builder = WMFileEventStreamRequestBuilder()
        builder.baseUrl(baseUrl)
        modify(builder)
        if (builder.hasAuthorizationHeader) {
            openEventStream(builder.build(), block)
            return
        }
        authorizedRequester.wrapAuthorizedOperation(null) { _, token ->
            openEventStream(builder.bearerAuthorization(token).build(), block)
        }
    }

    private suspend fun openEventStream(request: FSRequest<Nothing>, block: suspend ClientSSESession.() -> Unit) {
        httpClient.serverSentEvents(
            request = {
                url(urlString = request.url())
                request.headerValues("Authorization").firstOrNull()?.let { token ->
                    header("Authorization", token)
                }
            },
            block = block
        )
    }

    override suspend fun createFileUploadUpdateListener(eventStreamId: String, block: suspend (WMFileUploadUpdate) -> Unit) {
        createFileEventStream(modify = { eventStreamId(eventStreamId) }) {
            incoming.collect { event ->
                event.data?.let { data ->
                    val update = try {
                        Json.decodeFromString<KxsWMFileUploadUpdate>(data)
                    } catch (e: Exception) {
                        null
                    }
                    update?.let {
                        block(it)
                        if (!it.inProgress) {
                            cancel()
                        }
                    }
                }
            }
        }
    }

    override suspend fun fetchStoredMixJDFileUrls(
        modify: WMPresignedJDFileUrlsRequestBuilder.() -> Unit
    ): FSResponse<Map<String, WMJobDescriptionFile>, Nothing> {
        return createAndSendAuthenticatedRequestWithoutBody(
            createRequestBuilder = ::WMPresignedJDFileUrlsRequestBuilder,
            modifyRequest = modify,
            parseResponseBody = { response -> response.body<Map<String, KxsWMJobDescriptionFile>>() }
        )
    }

    override suspend fun createSharedMixUrl(
        modify: WMCreateSharedMixRequestBuilder.() -> Unit
    ): FSResponse<WMSharedMixResponse, WMSharedMixCreate> {
        return createAndSendAuthenticatedRequest(
            createRequestBuilder = ::WMCreateSharedMixRequestBuilder,
            modifyRequest = modify,
            setRequestBody = { setBody(it.toKxs()) },
            parseResponseBody = { response -> response.body<KxsWMSharedMixResponse>() }
        )
    }

    override suspend fun retryJobDescriptionUpload(
        modify: WMRetryUploadRequestBuilder.() -> Unit
    ): FSResponse<Unit, Nothing> {
        return createAndSendAuthenticatedRequestWithoutBody(
            createRequestBuilder = ::WMRetryUploadRequestBuilder,
            modifyRequest = modify,
            parseResponseBody = { null }
        )
    }

    override suspend fun deleteJobDescriptionUpload(
        modify: WMDeleteUploadRequestBuilder.() -> Unit
    ): FSResponse<Unit, Nothing> {
        return createAndSendAuthenticatedRequestWithoutBody(
            createRequestBuilder = ::WMDeleteUploadRequestBuilder,
            modifyRequest = modify,
            parseResponseBody = { null }
        )
    }

    override suspend fun setSharedMixActive(
        modify: WMSetSharedMixActiveRequestBuilder.() -> Unit
    ): FSResponse<Unit, WMSharedMixActivation> {
        return createAndSendAuthenticatedRequest(
            createRequestBuilder = ::WMSetSharedMixActiveRequestBuilder,
            modifyRequest = modify,
            setRequestBody = { setBody(it.toKxs()) },
            parseResponseBody = { null }
        )
    }

    override suspend fun regenerateSharedMixPreview(
        modify: WMRegenerateSharedMixPreviewRequestBuilder.() -> Unit
    ): FSResponse<Unit, Nothing> {
        return createAndSendAuthenticatedRequestWithoutBody(
            createRequestBuilder = ::WMRegenerateSharedMixPreviewRequestBuilder,
            modifyRequest = modify,
            parseResponseBody = { null }
        )
    }

    override suspend fun fetchPlanPrices(modify: WMPlanPricesRequestBuilder.() -> Unit): FSResponse<List<WMPlanPrice>, Nothing> {
        return createAndSendRequestWithoutBody(
            createRequestBuilder = ::WMPlanPricesRequestBuilder,
            modifyRequest = modify,
            parseResponseBody = { response -> response.body<List<KxsWMPlanPrice>>() }
        )
    }

    override suspend fun createCheckoutSession(
        modify: WMCreateCheckoutSessionRequestBuilder.() -> Unit
    ): FSResponse<WMRedirectUrl, WMCheckoutSessionCreate> {
        return createAndSendAuthenticatedRequest(
            createRequestBuilder = ::WMCreateCheckoutSessionRequestBuilder,
            modifyRequest = modify,
            setRequestBody = { setBody(it.toKxs()) },
            parseResponseBody = { response -> response.body<KxsWMRedirectUrl>() }
        )
    }

    override suspend fun createBillingPortalSession(
        modify: WMCreateBillingPortalSessionRequestBuilder.() -> Unit
    ): FSResponse<WMRedirectUrl, WMBillingPortalSessionCreate> {
        return createAndSendAuthenticatedRequest(
            createRequestBuilder = ::WMCreateBillingPortalSessionRequestBuilder,
            modifyRequest = modify,
            setRequestBody = { setBody(it.toKxs()) },
            parseResponseBody = { response -> response.body<KxsWMRedirectUrl>() }
        )
    }

    override suspend fun fetchCheckoutSession(modify: WMCheckoutSessionRequestBuilder.() -> Unit): FSResponse<WMCheckoutSummary, Nothing> {
        return createAndSendAuthenticatedRequestWithoutBody(
            createRequestBuilder = ::WMCheckoutSessionRequestBuilder,
            modifyRequest = modify,
            parseResponseBody = { response -> response.body<KxsWMCheckoutSummary>() }
        )
    }

    override suspend fun createAccount(
        modify: WMCreateAccountRequestBuilder.() -> Unit
    ): FSResponse<WMAccountCreated, WMAccountCreate> {
        return createAndSendRequest(
            createRequestBuilder = ::WMCreateAccountRequestBuilder,
            modifyRequest = modify,
            setRequestBody = { setBody(it.toKxs()) },
            parseResponseBody = { response -> response.body<KxsWMAccountCreated>() }
        )
    }

    override suspend fun resendEmailVerification(
        modify: WMResendEmailVerificationRequestBuilder.() -> Unit
    ): FSResponse<Unit, Nothing> {
        return createAndSendAuthenticatedRequestWithoutBody(
            createRequestBuilder = ::WMResendEmailVerificationRequestBuilder,
            modifyRequest = modify,
            parseResponseBody = { null }
        )
    }

    override suspend fun confirmEmailVerification(
        modify: WMConfirmEmailVerificationRequestBuilder.() -> Unit
    ): FSResponse<Unit, WMAuthActionToken> {
        return createAndSendRequest(
            createRequestBuilder = ::WMConfirmEmailVerificationRequestBuilder,
            modifyRequest = modify,
            setRequestBody = { setBody(it.toKxs()) },
            parseResponseBody = { null }
        )
    }

    override suspend fun requestPasswordReset(
        modify: WMRequestPasswordResetRequestBuilder.() -> Unit
    ): FSResponse<Unit, WMPasswordResetRequest> {
        return createAndSendRequest(
            createRequestBuilder = ::WMRequestPasswordResetRequestBuilder,
            modifyRequest = modify,
            setRequestBody = { setBody(it.toKxs()) },
            parseResponseBody = { null }
        )
    }

    override suspend fun verifyPasswordReset(
        modify: WMVerifyPasswordResetRequestBuilder.() -> Unit
    ): FSResponse<WMPasswordResetTarget, WMAuthActionToken> {
        return createAndSendRequest(
            createRequestBuilder = ::WMVerifyPasswordResetRequestBuilder,
            modifyRequest = modify,
            setRequestBody = { setBody(it.toKxs()) },
            parseResponseBody = { response -> response.body<KxsWMPasswordResetTarget>() }
        )
    }

    override suspend fun confirmPasswordReset(
        modify: WMConfirmPasswordResetRequestBuilder.() -> Unit
    ): FSResponse<Unit, WMPasswordResetConfirm> {
        return createAndSendRequest(
            createRequestBuilder = ::WMConfirmPasswordResetRequestBuilder,
            modifyRequest = modify,
            setRequestBody = { setBody(it.toKxs()) },
            parseResponseBody = { null }
        )
    }

    override suspend fun createMcpApiKey(
        modify: WMCreateMcpApiKeyRequestBuilder.() -> Unit
    ): FSResponse<WMMcpApiKeyCreated, WMMcpApiKeyCreate> {
        return createAndSendAuthenticatedRequest(
            createRequestBuilder = ::WMCreateMcpApiKeyRequestBuilder,
            modifyRequest = modify,
            setRequestBody = { setBody(it.toKxs()) },
            parseResponseBody = { response -> response.body<KxsWMMcpApiKeyCreated>() }
        )
    }

    override suspend fun fetchMcpApiKeys(
        modify: WMMcpApiKeysRequestBuilder.() -> Unit
    ): FSResponse<List<WMMcpApiKey>, Nothing> {
        return createAndSendAuthenticatedRequestWithoutBody(
            createRequestBuilder = ::WMMcpApiKeysRequestBuilder,
            modifyRequest = modify,
            parseResponseBody = { response -> response.body<List<KxsWMMcpApiKey>>() }
        )
    }

    override suspend fun revokeMcpApiKey(
        modify: WMRevokeMcpApiKeyRequestBuilder.() -> Unit
    ): FSResponse<Unit, Nothing> {
        return createAndSendAuthenticatedRequestWithoutBody(
            createRequestBuilder = ::WMRevokeMcpApiKeyRequestBuilder,
            modifyRequest = modify,
            parseResponseBody = { null }
        )
    }

    override suspend fun resolveMcpApiKey(
        modify: WMResolveMcpApiKeyRequestBuilder.() -> Unit
    ): FSResponse<WMMcpApiKeyOwner, WMMcpApiKeyResolve> {
        return createAndSendAuthenticatedRequest(
            createRequestBuilder = ::WMResolveMcpApiKeyRequestBuilder,
            modifyRequest = modify,
            setRequestBody = { setBody(it.toKxs()) },
            parseResponseBody = { response -> response.body<KxsWMMcpApiKeyOwner>() }
        )
    }
}
